import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { AccountType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { type JSX, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import { TextFormField } from '~components/FormFields/TextFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { CustomerAccountNotes } from '~components/Order/CustomerAccountNotes';
import { PhaseBadge, PhaseDrawer } from '~components/Order/PhaseDrawer/PhaseDrawer';
import type { PhaseOptions } from '~components/Order/PhaseDrawer/types';
import useAccount from '~hooks/useAccount/useAccount';
import type { CompanyBasic } from '~hooks/useCompany/models';
import type { BasicDepartment } from '~hooks/useDepartment/models';
import { useDepartment } from '~hooks/useDepartment/useDepartment';
import DepartmentInlineForm from '~pages/Settings/DepartmentManagement/DepartmentInlineForm';
import SuggestedRateDepartments from '~pages/Settings/DepartmentManagement/SuggestedRateDepartments';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

import type { ProjectFormSchemaInterface } from '../../projectFormSchema';
import { getPhaseIdentityKey } from '../phaseIdentity';
import { useProjectFormV2Context } from '../ProjectFormV2Context';

type PhaseFormValue = NonNullable<ProjectFormSchemaInterface['phases']>[number];

// Mirrors the legacy form. Render-only by design: all
// effects (cascading resets, FF reads) live in
// ProjectFormV2Provider. Local UI state — drawer open / inline-form open /
// account typeahead value — uses useState; the panel-side grep gate
// forbids the four reactive hooks (effects / watchers / flag
// reads) inside panel files.
function ProjectDetailsPanelComponent(): JSX.Element {
  const { control, formState, getValues, setValue, trigger } =
    useFormContext<ProjectFormSchemaInterface>();
  const {
    dispatchNumberLabel,
    flags,
    isDepartmentRequired,
    isRateByDepartmentNameRuleOn,
    showContractNumber,
    defaultProject,
    watchedAccount,
    watchedSelectedCompany,
  } = useProjectFormV2Context();
  const { toasterStore } = useStores();
  const { createCustomerAccountByName, getAccountsByCompanyIdTypeahead } = useAccount();
  const { getDepartmentByCompanyIdTypeahead } = useDepartment();

  const [showCreateDepartment, setShowCreateDepartment] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // tmpAccountName mirrors legacy state, kept local to this panel. It feeds
  // the "Create account" chip in `noOptionsText` below, which goes through
  // the typed createCustomerAccountByName — the legacy `as unknown as
  // Account` shortcut is forbidden by the v2 grep gate.
  const [tmpAccountName, setTmpAccountName] = useState('');
  const onInputAccountName = useCallback(
    (_event: React.SyntheticEvent, value: string) => setTmpAccountName(value),
    [],
  );

  // Mirrors the legacy form. shouldDirty is set because this
  // is a genuine user selection; trigger clears a lingering "required" error,
  // same as the department inline-create below. Failures already toast via
  // connection.handleRequestError inside the hook, so the catch only keeps
  // the chip alive for a retry.
  const createNewAccount = useCallback(async () => {
    const companyId = watchedSelectedCompany?.id;
    if (!companyId) {
      toasterStore.push(
        alert(t('form_validation_errors.no_user_company'), AlertTypes.error),
      );
      return;
    }

    try {
      const account = await createCustomerAccountByName({
        companyId,
        name: tmpAccountName,
      });

      toasterStore.push(
        alert(t('account.account_created', { name: account.name }), AlertTypes.success),
      );
      setValue('account', account, { shouldDirty: true });
      void trigger('account');
    } catch (error) {
      void error;
    }
  }, [
    createCustomerAccountByName,
    setValue,
    tmpAccountName,
    toasterStore,
    trigger,
    watchedSelectedCompany?.id,
  ]);

  // Phase widget: the sanctioned replacement is PhaseSelection → PhaseDrawer. The
  // panel maintains the local phases collection (seeded from the
  // defaultProject) and writes the canonical form value via setValue.
  const initialPhases = useMemo<PhaseOptions[]>(
    () =>
      defaultProject?.phases?.map((phase) => ({
        id: phase.id,
        name: phase.name,
        code: phase.code,
        primary: phase.primary,
      })) ?? [],
    [defaultProject?.phases],
  );
  const [localPhaseOptions, setLocalPhaseOptions] =
    useState<PhaseOptions[]>(initialPhases);

  const writePhases = useCallback(
    (next: PhaseOptions[]) => {
      setLocalPhaseOptions(next);
      const formPhases = next.map<PhaseFormValue>((value) => ({
        id: value.id,
        name: value.name,
        code: value.code,
        primary: value.primary,
        isDeleted: Boolean(value._destroy),
      }));
      setValue('phases', formPhases, { shouldDirty: true });

      // Cascade: clear `phase` on any projectMaterialTotal whose phase ref
      // no longer points at a non-deleted phase. Without this, the materials
      // row's "Phase Name" dropdown keeps rendering a phase the user just
      // removed (the autocomplete sees a value that isn't in `list`).
      // Mirrors the legacy form — we don't need v1's
      // previous-active-keys ref because we re-derive the active set from
      // `next` on every write.
      const activeKeys = new Set(
        next
          .filter((option) => !option._destroy)
          .map((option) => getPhaseIdentityKey(option))
          .filter((key): key is string => key !== null),
      );
      const materials = getValues('projectMaterialTotals') ?? [];
      let cascadeNeeded = false;
      const nextMaterials = materials.map((row) => {
        if (!row?.phase) return row;
        const rowKey = getPhaseIdentityKey(row.phase);
        if (rowKey && activeKeys.has(rowKey)) return row;
        cascadeNeeded = true;
        return { ...row, phase: null };
      });
      if (cascadeNeeded) {
        setValue('projectMaterialTotals', nextMaterials, { shouldDirty: true });
      }
    },
    [getValues, setValue],
  );

  const onPhaseSetPrimary = useCallback(
    (id: string) => {
      const next = localPhaseOptions.map((option) => ({
        ...option,
        primary: option.id === id ? !option.primary : false,
      }));
      writePhases(next);
    },
    [localPhaseOptions, writePhases],
  );

  const onPhaseUpdate = useCallback(
    (id: string, updates: { code: string; name: string }) => {
      const next = localPhaseOptions.map((option) =>
        option.id === id ? { ...option, ...updates } : option,
      );
      writePhases(next);
    },
    [localPhaseOptions, writePhases],
  );

  const onPhaseRemove = useCallback(
    (id: string) => {
      const next = localPhaseOptions
        .map((option) => (option.id === id ? { ...option, _destroy: 1 } : option))
        .filter((option) => !(option._destroy && option.id.startsWith('_temp_')));
      writePhases(next);
    },
    [localPhaseOptions, writePhases],
  );

  const onPhaseAdd = useCallback(
    (entries: { code: string; name: string }[]) => {
      // Match v1: when no phase exists yet, the very first phase entered is
      // auto-selected as the primary phase.
      const hasExistingPhase = localPhaseOptions.some((option) => !option._destroy);
      const newPhases: PhaseOptions[] = entries.map((entry, index) => ({
        id: `_temp_${crypto.randomUUID()}`,
        name: entry.name,
        code: entry.code,
        primary: !hasExistingPhase && index === 0,
      }));
      writePhases([...localPhaseOptions, ...newPhases]);
    },
    [localPhaseOptions, writePhases],
  );

  const phaseChipCount = localPhaseOptions.filter(
    (option) => !option._destroy && option.code,
  ).length;

  // Legacy showed `Phase Name` / `Phase Code` dropdowns; v2 surfaces the
  // primary phase as read-only text below the drawer legend (the drawer is
  // now the single place to pick/change the primary).
  const primaryPhase = localPhaseOptions.find(
    (option) => !option._destroy && option.primary,
  );

  return (
    <Card sx={{ p: 2, mb: 2, overflow: 'visible' }}>
      <Typography variant="form-section-title" sx={{ mb: 2 }}>
        {t('order.form.project_details')}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Box data-test-id="project-form-v2-company">
            <CompanySelectorFormField
              key={watchedSelectedCompany?.id}
              name="company"
              disabled={Boolean(defaultProject?.company?.id)}
              errors={formState.errors}
              control={control}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 6 }}>
          <Box data-test-id="project-form-v2-account">
            <AutocompleteAsyncFormField
              key={watchedSelectedCompany?.id ?? 'no-company'}
              dataUi="page-projects__project-form__customer-account-input"
              disabled={Boolean(defaultProject?.projectId || !watchedSelectedCompany?.id)}
              control={control}
              errors={formState.errors}
              name="account"
              label={t('dispatch.order.customer_account')}
              isRequired={true}
              getValue={(item) => item.id}
              getLabel={(item) => item.name || ''}
              asyncCallback={getAccountsByCompanyIdTypeahead}
              extraRequestOptions={{
                companyId: watchedSelectedCompany?.id || defaultProject?.company?.id,
                accountTypes: [AccountType.CUSTOMER],
              }}
              onInput={onInputAccountName}
              noOptionsText={
                <Chip
                  data-test-id="project-form-v2-account-create-chip"
                  size="small"
                  variant="filled"
                  color="primary"
                  onClick={() => void createNewAccount()}
                  sx={{ cursor: 'pointer' }}
                  label={t('account.create_account')}
                />
              }
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 6 }}>
          <TextFormField
            control={control}
            errors={formState.errors}
            name="name"
            label={t('project.form.project_name')}
            isRequired={true}
          />
        </Grid>

        <CustomerAccountNotes
          accountId={watchedAccount?.id}
          companyId={watchedSelectedCompany?.id}
        />

        <Grid size={{ xs: 6 }}>
          <Box data-test-id="project-form-v2-department">
            <AutocompleteAsyncFormField
              key={watchedSelectedCompany?.id ?? 'no-company'}
              control={control}
              name="department"
              errors={formState.errors}
              isRequired={isDepartmentRequired}
              sx={{ mr: 2 }}
              disabled={!watchedSelectedCompany?.id}
              asyncCallback={getDepartmentByCompanyIdTypeahead}
              extraRequestOptions={{ companyId: watchedSelectedCompany?.id }}
              label={t('project.form.department')}
              getValue={(item) => item.id}
              getLabel={(item) => item.name || ''}
              clearable={true}
              noOptionsText={
                <Chip
                  data-test-id="project-form-v2-department-create-chip"
                  size="small"
                  variant="filled"
                  color="primary"
                  onClick={() => setShowCreateDepartment(true)}
                  disabled={!watchedSelectedCompany?.id}
                  sx={{ cursor: 'pointer' }}
                  label={t('administration.department.create')}
                />
              }
            />
            <SuggestedRateDepartments
              companyId={watchedSelectedCompany?.id}
              isRateByDepartmentNameRuleOn={isRateByDepartmentNameRuleOn}
              onCreated={(department) => {
                setValue('department', department, { shouldDirty: true });
                void trigger('department');
              }}
              sx={{ mt: 1 }}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 6 }}>
          {flags.tomlinsonOrderModal ? null : (
            <TextFormField
              control={control}
              errors={formState.errors}
              name="externalId"
              label={t('project.form.project_external_id')}
              isRequired={false}
            />
          )}
        </Grid>

        {showCreateDepartment && (
          <Collapse in={showCreateDepartment} sx={{ width: '100%' }}>
            <Grid sx={{ ml: 2 }}>
              <DepartmentInlineForm
                connectedCompany={watchedSelectedCompany as CompanyBasic}
                onCreated={(department: BasicDepartment) => {
                  setShowCreateDepartment(false);
                  setValue('department', department, { shouldDirty: true });
                  void trigger('department');
                }}
                onClose={() => setShowCreateDepartment(false)}
              />
            </Grid>
          </Collapse>
        )}

        {showContractNumber && (
          <Grid size={{ xs: 6 }}>
            <TextFormField
              control={control}
              errors={formState.errors}
              name="contract_number"
              label={t('project.form.contract_number')}
              isRequired={false}
            />
          </Grid>
        )}

        <Grid size={{ xs: 6 }}>
          <TextFormField
            control={control}
            errors={formState.errors}
            name="dispatch_number"
            label={dispatchNumberLabel('project.form.dispatch_number_full')}
            isRequired={false}
          />
        </Grid>

        <Grid size={{ xs: 6 }}>
          <TextFormField
            control={control}
            errors={formState.errors}
            name="poJobNumber"
            label={t('project.form.po_job_number')}
            isRequired={false}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PseudoLink
              title={t('phases.view_bulk_add')}
              action={() => setDrawerOpen(true)}
              dataTestId="project-form-v2-phase-drawer-trigger"
              dataUi="project-form-v2__phase-drawer-cta"
            />
            <PhaseBadge count={phaseChipCount} />
          </Box>
        </Grid>

        <Grid size={{ xs: 6 }}>
          <Box data-test-id="project-form-v2-primary-phase-name">
            <FormFieldLabel label={t('project.form.project_phase_name')} />
            <Typography variant="body2">{primaryPhase?.name ?? '—'}</Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Box data-test-id="project-form-v2-primary-phase-code">
            <FormFieldLabel label={t('project.form.project_phase_code')} />
            <Typography variant="body2">{primaryPhase?.code ?? '—'}</Typography>
          </Box>
        </Grid>
      </Grid>

      {drawerOpen && (
        <PhaseDrawer
          open
          onClose={() => setDrawerOpen(false)}
          phases={localPhaseOptions}
          readOnly={false}
          onUpdatePhase={onPhaseUpdate}
          onRemovePhase={onPhaseRemove}
          onSetPrimary={onPhaseSetPrimary}
          onAddPhases={onPhaseAdd}
        />
      )}
    </Card>
  );
}

export const ProjectDetailsPanel = observer(ProjectDetailsPanelComponent);
